<template>
  <div class="robot-wrap">
    <!-- 右下角悬浮胶囊：极简鲸 · 可拖拽换位 -->
    <button
      v-if="!panelOpen"
      ref="fab"
      class="bot-fab"
      :style="fabStyle"
      :class="{ dragging: isDragging }"
      @pointerdown="onDown"
      @pointermove="onMove"
      @pointerup="onUp"
      @pointercancel="onUp"
    >
      <span class="bic">
        <svg viewBox="0 0 48 48" width="22" height="22" aria-hidden="true">
          <g class="wh-float">
            <g class="wh-spout">
              <path d="M20 12q-2-4 0-7" fill="none" stroke="#f2c48a" stroke-width="2.4" stroke-linecap="round"/>
              <circle class="wh-drop" cx="20" cy="4" r="1.6" fill="#f2c48a"/>
            </g>
            <g class="wh-spout d2">
              <path d="M26 12q2-3 1-6" fill="none" stroke="#f2c48a" stroke-width="2.2" stroke-linecap="round"/>
              <circle class="wh-drop d2" cx="27" cy="4" r="1.3" fill="#f2c48a"/>
            </g>
            <path class="wh-tail" d="M35 22q8-3 9-9 4 6-1 12 6-1 8-4-2 9-13 8z" fill="#ffffff"/>
            <path d="M4 30q0-14 20-14 21 0 23 12 1 8-8 11-14 4-26 1-9-3-9-10z" fill="#ffffff"/>
            <circle class="wh-eye" cx="13" cy="27" r="2.2" fill="#2d5095"/>
          </g>
        </svg>
      </span>
      <span class="btxt">合规AI助手</span>
    </button>

    <!-- 对话抽屉 -->
    <div v-else class="bot-panel" :style="panelStyle">
      <div class="bp-head">
        <div class="av">
          <svg viewBox="0 0 48 48" width="22" height="22" aria-hidden="true">
            <path d="M35 22q8-3 9-9 4 6-1 12 6-1 8-4-2 9-13 8z" fill="#ffffff"/>
            <path d="M4 30q0-14 20-14 21 0 23 12 1 8-8 11-14 4-26 1-9-3-9-10z" fill="#ffffff"/>
            <circle cx="13" cy="27" r="2.2" fill="#2d5095"/>
          </svg>
        </div>
        <div>
          <div class="t1">合规AI助手</div>
          <div class="t2">基于核证法规 · 回答均附引用</div>
        </div>
        <button class="close" @click="panelOpen = false" aria-label="关闭">✕</button>
      </div>

      <div class="bp-chips">
        <span v-for="q in quickQuestions" :key="q" @click="sendQuick(q)">{{ q }}</span>
      </div>

      <div class="bp-msgs" ref="msgBox">
        <div v-for="(m, i) in messages" :key="i" class="m" :class="m.role">
          <div class="av">
            <svg v-if="m.role === 'bot'" viewBox="0 0 48 48" width="15" height="15" aria-hidden="true">
              <path d="M4 30q0-14 20-14 21 0 23 12 1 8-8 11-14 4-26 1-9-3-9-10z" fill="#2d5095"/>
              <circle cx="13" cy="27" r="2.2" fill="#ffffff"/>
            </svg>
            <template v-else>我</template>
          </div>
          <div class="bub">
            <span v-html="m.text"></span>
            <div class="cite" v-if="m.citations && m.citations.length">
              <span v-for="c in m.citations" :key="c.id">
                {{ c.name }}（{{ c.effectiveDate }}）
              </span>
            </div>
          </div>
        </div>
        <div v-if="loading" class="m bot">
          <div class="av">
            <svg viewBox="0 0 48 48" width="15" height="15" aria-hidden="true">
              <path d="M4 30q0-14 20-14 21 0 23 12 1 8-8 11-14 4-26 1-9-3-9-10z" fill="#2d5095"/>
              <circle cx="13" cy="27" r="2.2" fill="#ffffff"/>
            </svg>
          </div>
          <div class="bub typing">思考中<span>...</span></div>
        </div>
      </div>

      <div class="bp-input">
        <input
          v-model="question"
          placeholder="输入你的合规问题，Enter 发送"
          @keyup.enter="send"
          :disabled="loading"
        />
        <button @click="send" :disabled="loading || !question.trim()">发送</button>
      </div>
    </div>
  </div>
</template>

<script>
import { AskAI } from "@/api/project";
import { getToken } from "@/utils/auth";

const POS_KEY = "hg_whale_pos";

export default {
  name: "RobotAssistant",
  data() {
    return {
      panelOpen: false,
      question: "",
      loading: false,
      messages: [],
      pos: null,
      isDragging: false,
      dragMoved: false,
      dragStart: { x: 0, y: 0, px: 0, py: 0 },
      quickQuestions: [
        "生成式AI服务需要备案吗？",
        "数据出境有哪些合规要求？",
        "AI训练数据如何合规获取？",
        "算法推荐需要备案吗？",
      ],
    };
  },
  computed: {
    fabStyle() {
      if (!this.pos) return { right: "22px", bottom: "22px" };
      return { left: this.pos.x + "px", top: this.pos.y + "px", right: "auto", bottom: "auto" };
    },
    panelStyle() {
      if (!this.pos || typeof window === "undefined") return {};
      const vw = window.innerWidth;
      const vh = window.innerHeight;
      const pw = Math.min(360, vw - 24);
      const ph = Math.min(520, vh - 110);
      let left = Math.max(8, Math.min(this.pos.x - 16, vw - pw - 8));
      let top = this.pos.y - ph - 12;
      if (top < 8) top = Math.min(this.pos.y + 52 + 12, vh - ph - 8);
      return { left: left + "px", top: top + "px", width: pw + "px" };
    },
  },
  mounted() {
    this.$nextTick(() => this.initPos());
    window.addEventListener("resize", this.onResize);
  },
  beforeDestroy() {
    window.removeEventListener("resize", this.onResize);
  },
  methods: {
    initPos() {
      const el = this.$refs.fab;
      const fw = el ? el.offsetWidth : 120;
      const fh = el ? el.offsetHeight : 40;
      let saved = null;
      try {
        saved = JSON.parse(localStorage.getItem(POS_KEY) || "null");
      } catch (e) {
        saved = null;
      }
      if (
        saved &&
        typeof saved.x === "number" &&
        typeof saved.y === "number" &&
        saved.x >= 0 && saved.y >= 0 &&
        saved.x <= window.innerWidth - 40 &&
        saved.y <= window.innerHeight - 40
      ) {
        this.pos = saved;
      } else {
        this.pos = {
          x: window.innerWidth - fw - 22,
          y: window.innerHeight - fh - 22,
        };
      }
      this.clampPos();
    },
    clampPos() {
      const el = this.$refs.fab;
      const fw = el ? el.offsetWidth : 120;
      const fh = el ? el.offsetHeight : 40;
      this.pos = {
        x: Math.max(8, Math.min(this.pos.x, window.innerWidth - fw - 8)),
        y: Math.max(8, Math.min(this.pos.y, window.innerHeight - fh - 8)),
      };
    },
    onResize() {
      if (this.pos) this.clampPos();
    },
    onDown(e) {
      this.isDragging = true;
      this.dragMoved = false;
      this.dragStart = { x: e.clientX, y: e.clientY, px: this.pos.x, py: this.pos.y };
      if (this.$refs.fab && this.$refs.fab.setPointerCapture) {
        try {
          this.$refs.fab.setPointerCapture(e.pointerId);
        } catch (err) {}
      }
    },
    onMove(e) {
      if (!this.isDragging) return;
      const dx = e.clientX - this.dragStart.x;
      const dy = e.clientY - this.dragStart.y;
      if (Math.abs(dx) > 4 || Math.abs(dy) > 4) this.dragMoved = true;
      if (!this.dragMoved) return;
      this.pos = { x: this.dragStart.px + dx, y: this.dragStart.py + dy };
    },
    onUp() {
      if (!this.isDragging) return;
      this.isDragging = false;
      if (this.dragMoved) {
        this.clampPos();
        try {
          localStorage.setItem(POS_KEY, JSON.stringify(this.pos));
        } catch (e) {}
      } else {
        this.openPanel();
      }
      this.dragMoved = false;
    },
    ensureLogin() {
      if (!getToken()) {
        this.$router.push("/login");
        return false;
      }
      return true;
    },
    openPanel() {
      this.panelOpen = true;
      if (!this.messages.length) {
        this.messages.push({
          role: "bot",
          text:
            "您好，我是合规AI助手，可回答 AI 应用合规问题。我的回答仅依据平台已核证入库的法规原文，并附引用出处。",
          citations: [],
        });
      }
      this.scrollBottom();
    },
    sendQuick(q) {
      this.question = q;
      this.send();
    },
    send() {
      if (!this.question.trim()) return;
      if (!this.ensureLogin()) return;
      this.messages.push({ role: "user", text: this.escapeHtml(this.question.trim()), citations: [] });
      const q = this.question;
      this.question = "";
      this.loading = true;
      this.scrollBottom();
      AskAI({ question: q })
        .then((res) => {
          if (res && res.data) {
            this.messages.push({
              role: "bot",
              text: (res.data.answer || "").replace(/\n/g, "<br>"),
              citations: res.data.citations || [],
            });
          }
        })
        .catch(() => {
          this.messages.push({
            role: "bot",
            text: "AI助手暂时无法回答，请稍后重试。",
            citations: [],
          });
        })
        .finally(() => {
          this.loading = false;
          this.scrollBottom();
        });
    },
    escapeHtml(s) {
      return String(s)
        .replace(/&/g, "&amp;")
        .replace(/</g, "&lt;")
        .replace(/>/g, "&gt;")
        .replace(/"/g, "&quot;");
    },
    scrollBottom() {
      this.$nextTick(() => {
        if (this.$refs.msgBox) {
          this.$refs.msgBox.scrollTop = this.$refs.msgBox.scrollHeight;
        }
      });
    },
  },
};
</script>

<style lang="scss" scoped>
.robot-wrap {
  font-family: -apple-system, "PingFang SC", "Microsoft YaHei", sans-serif;
}

/* 悬浮胶囊：紧凑版 + 可拖拽 */
.bot-fab {
  position: fixed;
  display: flex;
  align-items: center;
  gap: 6px;
  background: linear-gradient(135deg, #2d5095, #24427c);
  color: #fff;
  border: none;
  border-radius: 999px;
  padding: 6px 13px 6px 8px;
  cursor: grab;
  box-shadow: 0 6px 18px rgba(33, 73, 184, 0.38);
  z-index: 1999;
  font-size: 12.5px;
  font-weight: 600;
  touch-action: none;
  user-select: none;
  -webkit-user-select: none;
  transition: box-shadow 0.2s;

  &:hover {
    box-shadow: 0 8px 22px rgba(33, 73, 184, 0.5);
  }

  &.dragging {
    cursor: grabbing;
    opacity: 0.92;
    box-shadow: 0 12px 28px rgba(33, 73, 184, 0.55);
  }

  .bic {
    width: 24px;
    height: 24px;
    border-radius: 50%;
    background: rgba(255, 255, 255, 0.16);
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
  }
}

/* 极简鲸动效（动感加强版） */
.wh-float {
  animation: whFloat 2.4s ease-in-out infinite;
  transform-box: view-box;
  transform-origin: center;
}

.wh-spout {
  animation: whSpout 1.8s ease-in-out infinite;
  transform-box: view-box;
  transform-origin: 20px 12px;

  &.d2 {
    animation-delay: 0.3s;
    transform-origin: 26px 12px;
  }
}

.wh-drop {
  animation: whDrop 1.8s ease-out infinite;
  transform-box: fill-box;
  transform-origin: center;

  &.d2 {
    animation-delay: 0.3s;
  }
}

.wh-tail {
  animation: whTail 1.2s ease-in-out infinite;
  transform-box: view-box;
  transform-origin: 35px 24px;
}

.wh-eye {
  animation: whBlink 2.8s infinite;
  transform-box: fill-box;
  transform-origin: center;
}

@keyframes whFloat {
  0%,
  100% {
    transform: translateY(0) rotate(0deg);
  }
  50% {
    transform: translateY(-3px) rotate(-3deg);
  }
}

@keyframes whSpout {
  0%,
  100% {
    opacity: 0.2;
  }
  45% {
    opacity: 1;
  }
}

@keyframes whDrop {
  0% {
    opacity: 0;
    transform: translateY(3px);
  }
  35% {
    opacity: 1;
  }
  100% {
    opacity: 0;
    transform: translateY(-5px);
  }
}

@keyframes whTail {
  0%,
  100% {
    transform: rotate(0deg);
  }
  50% {
    transform: rotate(-16deg);
  }
}

@keyframes whBlink {
  0%,
  88%,
  100% {
    transform: scaleY(1);
  }
  93% {
    transform: scaleY(0.15);
  }
}

/* 抽屉 */
.bot-panel {
  position: fixed;
  max-width: calc(100vw - 24px);
  max-height: min(520px, calc(100vh - 110px));
  background: #fff;
  border-radius: 16px;
  box-shadow: 0 22px 60px rgba(15, 23, 42, 0.28);
  border: 1px solid #e2e8f0;
  z-index: 2000;
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

.bp-head {
  background: linear-gradient(135deg, #0d1b38, #24427c);
  color: #fff;
  padding: 12px 15px;
  display: flex;
  align-items: center;
  gap: 10px;

  .av {
    width: 32px;
    height: 32px;
    border-radius: 50%;
    background: rgba(255, 255, 255, 0.18);
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
  }

  .t1 {
    font-size: 14px;
    font-weight: 700;
  }

  .t2 {
    font-size: 10.5px;
    opacity: 0.8;
    margin-top: 2px;
  }

  .close {
    margin-left: auto;
    width: 26px;
    height: 26px;
    border-radius: 50%;
    background: rgba(255, 255, 255, 0.14);
    border: none;
    color: #fff;
    font-size: 13px;
    cursor: pointer;
    line-height: 1;

    &:hover {
      background: rgba(255, 255, 255, 0.28);
    }
  }
}

.bp-chips {
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
  padding: 10px 13px 4px;

  span {
    font-size: 11px;
    color: #24427c;
    background: #e8eef9;
    border: 1px solid #d4e0f7;
    padding: 4px 11px;
    border-radius: 999px;
    cursor: pointer;
    transition: all 0.15s;

    &:hover {
      background: #e2ecfc;
      border-color: #2d5095;
    }
  }
}

.bp-msgs {
  flex: 1;
  overflow-y: auto;
  padding: 10px 13px;
  background: #f8fafc;
  min-height: 180px;
  max-height: 320px;
}

.m {
  display: flex;
  margin-bottom: 11px;
  gap: 8px;

  .av {
    width: 24px;
    height: 24px;
    border-radius: 50%;
    flex-shrink: 0;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 12px;
  }

  .bub {
    max-width: 78%;
    padding: 9px 12px;
    border-radius: 12px;
    font-size: 12.5px;
    line-height: 1.7;
    word-break: break-word;
  }

  &.bot {
    .av {
      background: #e8eef9;
    }

    .bub {
      background: #fff;
      border: 1px solid #e2e8f0;
      border-top-left-radius: 3px;
      color: #1e293b;
    }
  }

  &.user {
    flex-direction: row-reverse;

    .av {
      background: #2d5095;
      color: #fff;
    }

    .bub {
      background: #2d5095;
      color: #fff;
      border-top-right-radius: 3px;
    }
  }
}

.typing {
  color: #94a3b8;
  font-style: italic;
}

.cite {
  display: flex;
  flex-wrap: wrap;
  gap: 4px;
  margin-top: 8px;

  span {
    font-size: 10.5px;
    color: #24427c;
    background: #e8eef9;
    padding: 2px 8px;
    border-radius: 999px;
  }
}

.bp-input {
  display: flex;
  gap: 8px;
  padding: 11px 13px;
  border-top: 1px solid #e2e8f0;
  background: #fff;

  input {
    flex: 1;
    border: 1.5px solid #e2e8f0;
    border-radius: 10px;
    padding: 9px 12px;
    font-size: 13px;
    outline: none;
    transition: border-color 0.2s;

    &:focus {
      border-color: #2d5095;
    }

    &:disabled {
      background: #f8fafc;
      cursor: not-allowed;
    }
  }

  button {
    background: #2d5095;
    color: #fff;
    border: none;
    border-radius: 10px;
    padding: 0 16px;
    font-size: 13px;
    font-weight: 600;
    cursor: pointer;
    transition: background 0.2s;

    &:hover:not(:disabled) {
      background: #24427c;
    }

    &:disabled {
      opacity: 0.5;
      cursor: not-allowed;
    }
  }
}
</style>
